// components/LocaleClientEffects.jsx
'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'

// Ya NO importamos i18n aquí - el idioma viene configurado del servidor

const isAuth = (path) =>
  /^\/(?:[a-z]{2}(?:-[A-Z]{2})?)?\/auth(?:\/|$)/i.test(path || '/')

export default function LocaleClientEffects({ locale }) {
  const path = usePathname() || '/'

  // ❌ REMOVIDO: Ya no necesitamos cambiar el idioma de i18n
  // El TranslationsProvider ya recibe las traducciones correctas del servidor
  // useEffect(() => {
  //   i18n.changeLanguage((locale ?? 'es').toString())
  // }, [locale])

  // UI + WOW init (evitar en /auth/*)
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isAuth(path)) return

    // Importar módulos de forma lazy para no bloquear el render inicial
    const loadEffects = async () => {
      try {
        const [
          { init_wow },
          { parallaxMouseMovement, parallaxScroll },
          { headerChangeOnScroll }
        ] = await Promise.all([
          import('@/utils/initWowjs'),
          import('@/utils/parallax'),
          import('@/utils/changeHeaderOnScroll')
        ])

        init_wow()
        parallaxMouseMovement()

        const mainNav = document.querySelector('.main-nav')
        if (mainNav?.classList) {
          if (mainNav.classList.contains('transparent')) {
            mainNav.classList.add('js-transparent')
          } else if (!mainNav.classList.contains('dark')) {
            mainNav.classList.add('js-no-transparent-white')
          }
          window.addEventListener('scroll', headerChangeOnScroll)
        }

        parallaxScroll()
      } catch (e) {
        console.warn('UI effect failed:', e)
      }
    }

    // Usar requestIdleCallback para no bloquear el LCP
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => loadEffects(), { timeout: 2000 })
    } else {
      // Fallback para Safari
      setTimeout(loadEffects, 100)
    }

    return () => {
      import('@/utils/changeHeaderOnScroll').then(({ headerChangeOnScroll }) => {
        window.removeEventListener('scroll', headerChangeOnScroll)
      }).catch(() => {})
    }
  }, [path])

  // WOW sync por ruta
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isAuth(path)) return

    const syncWow = async () => {
      try {
        const { wow_sync } = await import('@/utils/initWowjs')
        requestAnimationFrame(() => wow_sync())
      } catch {}
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => syncWow(), { timeout: 1000 })
    } else {
      setTimeout(syncWow, 200)
    }
  }, [path])

  // WOW auto-sync por nuevos nodos .wow
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isAuth(path)) return

    let rafId = 0
    let wowSyncFn = null

    // Cargar wow_sync lazily
    import('@/utils/initWowjs').then(({ wow_sync }) => {
      wowSyncFn = wow_sync
    }).catch(() => {})

    const schedule = () => {
      if (!wowSyncFn) return
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => wowSyncFn())
    }

    const hasWow = (node) => {
      if (!(node instanceof Element)) return false
      if (node.classList?.contains('wow')) return true
      return !!node.querySelector?.('.wow')
    }

    const observer = new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (hasWow(n)) {
            schedule()
            return
          }
        }
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('load', schedule, { once: true })

    return () => {
      observer.disconnect()
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [path])

  // Bootstrap JS removido: ningún componente usa data-bs-* ni la API JS de
  // bootstrap (modales/FAQ/lightbox son estado React). Se importaba en cada
  // página sin uso (~24 KB gz de bootstrap.esm + Popper).

  return null
}
